/**
 * Detect "stale bundle" failures — the classic symptom of a service-worker
 * or HTTP cache pinning a user on an older deploy whose lazy chunks have
 * since been deleted. When we see one, we:
 *
 *   1. Show a one-line notice so the reload isn't silent
 *   2. Unregister any service workers + wipe all Cache Storage entries
 *   3. Hard-reload with a cache-busting query param
 *
 * Guarded by a 60s cooldown stored in localStorage so a misclassified error
 * can never trap the user in a reload loop.
 */

import { toast } from "sonner";

const COOLDOWN_KEY = "cp_last_stale_chunk_recovery";
const COOLDOWN_MS = 60_000;
const NOTICE_MS = 1200;

const STALE_PATTERN =
  /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk \d+ failed|error loading dynamically imported module/i;

export const isStaleBundleError = (input: unknown): boolean => {
  if (!input) return false;
  if (input instanceof Error) {
    return STALE_PATTERN.test(
      `${input.name || ""} ${input.message || ""} ${input.stack || ""}`,
    );
  }
  if (typeof input === "string") return STALE_PATTERN.test(input);
  if (typeof input === "object") {
    const anyInput = input as { message?: unknown; reason?: unknown };
    if (typeof anyInput.message === "string" && STALE_PATTERN.test(anyInput.message)) {
      return true;
    }
    if (anyInput.reason) return isStaleBundleError(anyInput.reason);
  }
  return false;
};

const clearAppCaches = async (): Promise<void> => {
  if (typeof window === "undefined") return;
  await Promise.allSettled([
    "serviceWorker" in navigator
      ? navigator.serviceWorker
          .getRegistrations()
          .then((regs) => Promise.all(regs.map((r) => r.unregister())))
      : Promise.resolve(),
    "caches" in window
      ? caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
      : Promise.resolve(),
  ]);
};

let recovering = false;

/**
 * Trigger the recovery flow. Safe to call from multiple error sources —
 * the first call wins, subsequent calls are dropped until the page reloads.
 * Returns true if recovery was started, false if it was suppressed by the
 * cooldown (so the caller can fall back to a normal error UI).
 */
export const recoverFromStaleBundle = (): boolean => {
  if (typeof window === "undefined") return false;
  if (recovering) return true;

  let last = 0;
  try {
    last = Number(localStorage.getItem(COOLDOWN_KEY) || "0");
  } catch {
    // localStorage may throw in private modes — treat as no cooldown.
  }
  if (Date.now() - last < COOLDOWN_MS) return false;

  recovering = true;
  try {
    localStorage.setItem(COOLDOWN_KEY, String(Date.now()));
  } catch {
    // non-fatal
  }

  try {
    toast.message("Updating to the latest version…", {
      description: "We'll reload the page in a moment.",
      duration: NOTICE_MS + 500,
    });
  } catch {
    // toast may not be mounted yet — recovery still proceeds.
  }

  void (async () => {
    await clearAppCaches();
    // Small delay so the toast is visible before the reload.
    setTimeout(() => {
      const url = new URL(window.location.href);
      url.searchParams.set("sw-cleanup", Date.now().toString());
      window.location.replace(url.toString());
    }, NOTICE_MS);
  })();

  return true;
};

/**
 * Wire up global listeners that catch dynamic-import failures React's error
 * boundary doesn't see — e.g. a chunk fetched during a lazy() resolve, a
 * navigation that flips into Suspense, or an unhandled promise from a
 * route guard.
 */
export const installStaleBundleListeners = (): void => {
  if (typeof window === "undefined") return;

  window.addEventListener("error", (event) => {
    if (isStaleBundleError(event.error) || isStaleBundleError(event.message)) {
      recoverFromStaleBundle();
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (isStaleBundleError(event.reason)) {
      recoverFromStaleBundle();
    }
  });
};
